import { useCallback, useEffect, useSyncExternalStore } from 'react'
import { safeStorage } from './utils'

export type ThemeMode = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'bixo.theme'
const listeners = new Set<() => void>()

function readMode(): ThemeMode {
  const stored = safeStorage.get(STORAGE_KEY)
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system'
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') return systemPrefersDark() ? 'dark' : 'light'
  return mode
}

/** Apply the stored theme to <html>. Call once before React renders to avoid a flash. */
export function initTheme(): void {
  document.documentElement.classList.toggle('dark', resolveTheme(readMode()) === 'dark')
}

function setMode(mode: ThemeMode): void {
  safeStorage.set(STORAGE_KEY, mode)
  initTheme()
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useTheme(): {
  mode: ThemeMode
  resolved: 'light' | 'dark'
  setMode: (mode: ThemeMode) => void
  toggle: () => void
} {
  const mode = useSyncExternalStore(subscribe, readMode, () => 'system' as const)

  useEffect(() => {
    if (mode !== 'system') return
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (): void => {
      initTheme()
      listeners.forEach((listener) => listener())
    }
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [mode])

  const resolved = resolveTheme(mode)
  const toggle = useCallback(() => setMode(resolved === 'dark' ? 'light' : 'dark'), [resolved])

  return { mode, resolved, setMode, toggle }
}
